import {groupBy} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {Badge, createStyles, Tooltip} from '@mantine/core';
import React from 'react';

import {DataFrameColumnDef, DataFrameRow} from 'data-frames/DataFrameTypes';
import {DataFrameRenderOverrides} from 'data-frames/table/DataFrameTable';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineStepConfig} from 'pipelines/PipelineTypes';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {JSONValue} from 'util/Types';

/**
 * The displayOptions portion of the TABLE_VIEW step config, derived from the step shape library.
 */
export type TableViewDisplayOptions = NonNullable<
  PipelineStepConfig<PipelineStepType.TABLE_VIEW, 0>['displayOptions']
>;

/**
 * Builds DataFrameRenderOverrides from TABLE_VIEW display options configuration.
 *
 * For each columnIconDecoration:
 * - Renders the specified icon badge next to cell values in the target column when the boolean column is true
 * - Renders a count of true values + icon badge in the target column header (only when all rows are loaded)
 *
 * Also hides any columns listed in hiddenColumns from rendering.
 *
 * @param totalRows - The total number of rows in the data frame. When this exceeds `rows.length`,
 *   header badge counts are suppressed since they would be inaccurate.
 */
export default function getTableViewDisplayOptionsOverrides(
  displayOptions: TableViewDisplayOptions,
  rows: ReadonlyArray<DataFrameRow>,
  totalRows?: number
): DataFrameRenderOverrides {
  const {columnIconDecorations, hiddenColumns} = displayOptions;

  const allRowsLoaded = totalRows == null || totalRows <= rows.length;
  const booleanColumnCounts = allRowsLoaded ? computeBooleanColumnCounts(columnIconDecorations, rows) : null;
  const decorationsByTarget = groupBy(columnIconDecorations, (d) => d.targetColumn);

  const columnNameToHeaderDecorationRenderFn: Record<string, (column: DataFrameColumnDef) => JSX.Element> =
    {};
  const columnNameToDataCellDecorationRenderFn: Record<
    string,
    (cellValue: JSONValue, row: Record<string, JSONValue>) => JSX.Element
  > = {};

  for (const targetColumn of Object.keys(decorationsByTarget)) {
    const decorations = decorationsByTarget[targetColumn];

    columnNameToDataCellDecorationRenderFn[targetColumn] = (
      _cellValue: JSONValue,
      row: Record<string, JSONValue>
    ) => {
      const activeDecorations = decorations.filter((d) => row[d.booleanColumn] === true);
      return <CellDecorationBadges activeDecorations={activeDecorations} />;
    };

    columnNameToHeaderDecorationRenderFn[targetColumn] = () => {
      const iconCounts = decorations.map((decoration) => ({
        icon: decoration.icon,
        count: booleanColumnCounts != null ? (booleanColumnCounts[decoration.booleanColumn] ?? 0) : null,
        booleanColumn: decoration.booleanColumn,
        tooltip: decoration.headerTooltip,
      }));
      return <HeaderDecorationBadges iconCounts={iconCounts} />;
    };
  }

  return {
    columnNameToHeaderDecorationRenderFn,
    columnNameToDataCellDecorationRenderFn,
    hiddenColumnNames: new Set<string>(hiddenColumns ?? []),
  };
}

/**
 * Precompute true-counts for each boolean column referenced by decorations.
 */
function computeBooleanColumnCounts(
  decorations: TableViewDisplayOptions['columnIconDecorations'],
  rows: ReadonlyArray<DataFrameRow>
): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const d of decorations) {
    if (counts[d.booleanColumn] == null) {
      counts[d.booleanColumn] = 0;
    }
  }
  for (const row of rows) {
    for (const boolCol of Object.keys(counts)) {
      if (row[boolCol] === true) {
        counts[boolCol]++;
      }
    }
  }
  return counts;
}

const BADGE_ROOT_STYLES = {color: Color.GRAY_600, backgroundColor: Color.GRAY_200};

const useStyles = createStyles((theme) => ({
  badgeGroup: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
    marginLeft: theme.spacing.xs,
  },
  badgeCount: {
    fontSize: theme.fontSizes.xs,
  },
  icon: {
    '&&': {width: theme.spacing.md, height: theme.spacing.md},
  },
  tooltip: {
    fontWeight: 400,
    width: 300,
    whiteSpace: 'normal',
    wordBreak: 'normal',
  },
}));

function CellDecorationBadges({
  activeDecorations,
}: {
  activeDecorations: TableViewDisplayOptions['columnIconDecorations'];
}): JSX.Element {
  const {classes} = useStyles();

  if (activeDecorations.length === 0) {
    return <></>;
  }

  return (
    <span className={classes.badgeGroup}>
      {activeDecorations.map((decoration) => {
        const key = `${decoration.targetColumn}-${decoration.booleanColumn}-${decoration.icon}`;
        return (
          <Tooltip
            key={key}
            label={decoration.tooltip}
            disabled={decoration.tooltip == null}
            classNames={{tooltip: classes.tooltip}}
          >
            <Badge
              color="gray"
              size="md"
              variant="filled"
              styles={() => ({
                root: BADGE_ROOT_STYLES,
              })}
            >
              <BenchlingIcon className={classes.icon} icon={decoration.icon as BenchlingIconName} />
            </Badge>
          </Tooltip>
        );
      })}
    </span>
  );
}

function HeaderDecorationBadges({
  iconCounts,
}: {
  iconCounts: Array<{icon: string; count: number | null; booleanColumn: string; tooltip?: string | null}>;
}): JSX.Element {
  const {classes} = useStyles();

  const visibleIconCounts = iconCounts.filter(({count}) => count == null || count > 0);
  if (visibleIconCounts.length === 0) {
    return <></>;
  }

  return (
    <span className={classes.badgeGroup}>
      {visibleIconCounts.map(({icon, count, booleanColumn, tooltip}) => (
        <Tooltip
          key={booleanColumn}
          label={tooltip}
          disabled={tooltip == null}
          classNames={{tooltip: classes.tooltip}}
        >
          <Badge
            key={booleanColumn}
            color="gray"
            size="md"
            variant="filled"
            styles={() => ({
              root: BADGE_ROOT_STYLES,
            })}
            rightSection={count != null ? <BenchlingIcon icon={icon as BenchlingIconName} /> : undefined}
          >
            {count != null && count > 0 && <span className={classes.badgeCount}>{count}</span>}
            {count == null && <BenchlingIcon className={classes.icon} icon={icon as BenchlingIconName} />}
          </Badge>
        </Tooltip>
      ))}
    </span>
  );
}
